import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, Eye, Share2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import FastIdUploadForm from '../components/fastId/FastIdUploadForm';
import { copyFastIdReportLink } from '../components/fastId/FastIdReportView';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../services/permissions';
import { FAST_ID_REPORT_LIST_PAGE_KEY, listPageState, useListPage } from '../utils/listPage';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';

const HISTORY_FETCH_LIMIT = 200;

const getRowKey = (row) => `fastid-${row.id}`;

const FastIdReport = () => {
  const { user: currentUser } = useAuth();
  const { can } = usePermissions();
  const canRunFastId = can(PERMISSIONS.FASTID_RUN);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [organizationsLoading, setOrganizationsLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  /** null = default (upload time, most recent first) */
  const [userSortDir, setUserSortDir] = useState(null);
  const loadRef = useRef(0);
  const { currentPage, goToPage } = useListPage({ storageKey: FAST_ID_REPORT_LIST_PAGE_KEY });

  const selectedOrganizationId = organizations.find((o) => o.name === selectedOrganization)?.id;

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await organizationsService.getOrganizations();
        setOrganizations(orgs);
        const savedSelected = organizationsService.getSelectedOrganization('name');
        if (savedSelected && orgs.some((o) => o.name === savedSelected)) {
          setSelectedOrganization(savedSelected);
        } else if (orgs.length > 0) {
          const defaultOrg = orgs[0].name;
          setSelectedOrganization(defaultOrg);
          organizationsService.setSelectedOrganization(defaultOrg, 'name', orgs);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
      } finally {
        setOrganizationsLoading(false);
      }
    };
    loadOrganizations();
  }, []);

  const loadFastIdHistory = useCallback(async () => {
    if (!currentUser || !selectedOrganizationId) return;
    const generation = loadRef.current + 1;
    loadRef.current = generation;
    setLoading(true);
    try {
      const res = await api.get('/api/v2/fast_id_uploads', {
        params: { organization_id: selectedOrganizationId, limit: HISTORY_FETCH_LIMIT },
      });
      if (loadRef.current !== generation) return;
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      if (loadRef.current !== generation) return;
      console.error('FastID report history error:', error);
      setRows([]);
    } finally {
      if (loadRef.current === generation) setLoading(false);
    }
  }, [currentUser, selectedOrganizationId]);

  useEffect(() => {
    loadFastIdHistory();
  }, [loadFastIdHistory]);

  const sortedRows = useMemo(() => {
    if (!userSortDir) return rows;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const withDir = userSortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => withDir * collator.compare(a.user_name || '', b.user_name || ''));
  }, [rows, userSortDir]);

  const toggleUserSort = () => {
    setUserSortDir((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
  };

  const listOffset = (currentPage - 1) * LIST_PAGE_SIZE;
  const pagedRows = sortedRows.slice(listOffset, listOffset + LIST_PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(rows.length / LIST_PAGE_SIZE) || 1);

  useEffect(() => {
    if (loading || rows.length === 0) return;
    if (currentPage > totalPages) {
      goToPage(totalPages);
    }
  }, [loading, rows.length, currentPage, totalPages, goToPage]);

  // Reset to page 1 when the user switches org (not on the initial '' -> saved-org assignment,
  // which would otherwise clobber a page restored via the report page's back button).
  const prevOrganizationRef = useRef(selectedOrganization);
  useEffect(() => {
    if (prevOrganizationRef.current && prevOrganizationRef.current !== selectedOrganization) {
      goToPage(1);
      setExpandedId(null);
    }
    prevOrganizationRef.current = selectedOrganization;
  }, [selectedOrganization, goToPage]);

  return (
    <div className="space-y-4">
      <PageHeaderActions>
        <div className={HEADER_SELECT_CLASS}>
          {organizationsLoading || organizations.length === 0 ? (
            <FunctionalSelect
              value=""
              onValueChange={() => {}}
              placeholder={organizationsLoading ? 'Loading...' : 'No organizations'}
              className="w-full"
              disabled
              label="Organization"
            />
          ) : (
            <FunctionalSelect
              id="fastid-report-organization"
              value={organizationsService.getSelectedOptionValue(organizations, selectedOrganization, 'name')}
              onValueChange={(value) => {
                const parsed = organizationsService.parseOrganizationOptionValue(value);
                const name = parsed?.name || value;
                setSelectedOrganization(name);
                organizationsService.setSelectedOrganization(name, 'name', parsed ? [{ name, id: parsed.id }] : organizations);
              }}
              placeholder="Select organization"
              className="w-full"
              label="Organization"
            >
              {organizations.map((org) => (
                <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                  {org.name}
                </SelectItem>
              ))}
            </FunctionalSelect>
          )}
        </div>
      </PageHeaderActions>

      {canRunFastId && (
        <FastIdUploadForm
          organizations={organizations}
          selectedOrganization={selectedOrganization}
          organizationsLoading={organizationsLoading}
          onUploadStarted={loadFastIdHistory}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Uploads</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500">Loading FastID uploads...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">No Fast-ID uploads yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200 rounded-md">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left font-medium text-gray-700 border-b w-9" aria-label="Expand" />
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Upload time</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                        <button
                          type="button"
                          onClick={toggleUserSort}
                          className="inline-flex items-center gap-1 hover:text-gray-900"
                        >
                          User
                          {userSortDir === 'asc' ? (
                            <ChevronUp size={14} />
                          ) : userSortDir === 'desc' ? (
                            <ChevronDown size={14} />
                          ) : (
                            <ChevronsUpDown size={14} className="text-gray-400" />
                          )}
                        </button>
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">File name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Organization</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b min-w-[9rem]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => {
                      const rowKey = getRowKey(row);
                      const showReportButton =
                        row.processing_status === 'complete' && Boolean(row.execution_id);
                      return (
                        <React.Fragment key={rowKey}>
                          <tr
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                            onClick={() => setExpandedId((prev) => (prev === rowKey ? null : rowKey))}
                          >
                            <td className="px-2 py-2 text-gray-500" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setExpandedId((prev) => (prev === rowKey ? null : rowKey))}
                                className="p-0.5 rounded hover:bg-gray-200"
                                aria-expanded={expandedId === rowKey}
                              >
                                {expandedId === rowKey ? (
                                  <ChevronDown size={18} />
                                ) : (
                                  <ChevronRight size={18} />
                                )}
                              </button>
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {row.upload_time ? new Date(row.upload_time).toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-800">{row.user_name || '—'}</td>
                            <td className="px-3 py-2 text-gray-800">{row.file_name || '—'}</td>
                            <td className="px-3 py-2 text-gray-800">{row.organization_name || '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                row.processing_status === 'started' || row.processing_status === 'validated'
                                  ? 'bg-green-100 text-green-800' :
                                row.processing_status === 'complete' ? 'bg-blue-100 text-blue-800' :
                                row.processing_status === 'error' ? 'bg-red-100 text-red-800' :
                                row.processing_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {row.processing_status || '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                              {showReportButton ? (
                                <div className="flex items-center gap-1">
                                  <Link
                                    to={`/fastid-report/${row.execution_id}`}
                                    state={listPageState(currentPage)}
                                    className="p-1.5 rounded shrink-0 inline-flex text-[#0062FB] hover:bg-blue-50"
                                    aria-label={`View Fast-ID report for ${row.file_name || row.execution_id}`}
                                    title="View report"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() => copyFastIdReportLink(row.execution_id, selectedOrganizationId)}
                                    className="p-1.5 rounded shrink-0 inline-flex text-[#0062FB] hover:bg-blue-50"
                                    aria-label={`Copy report link for ${row.file_name || row.execution_id}`}
                                    title="Copy report link"
                                  >
                                    <Share2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">—</span>
                              )}
                            </td>
                          </tr>
                          {expandedId === rowKey && (
                            <tr key={`${rowKey}-expanded`} className="border-b border-gray-100 bg-gray-50">
                              <td colSpan={7} className="px-3 py-2">
                                <div className="text-xs text-gray-600 space-y-1">
                                  {row.execution_id && (
                                    <div>
                                      <span className="font-medium text-gray-700">Execution ID:</span>{' '}
                                      <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.execution_id}>
                                        {row.execution_id}
                                      </code>
                                    </div>
                                  )}
                                  {row.upload_id != null && (
                                    <div>
                                      <span className="font-medium text-gray-700">Upload ID:</span>{' '}
                                      <code className="break-all bg-gray-100 px-2 py-1 rounded" title={String(row.upload_id)}>
                                        {row.upload_id}
                                      </code>
                                    </div>
                                  )}
                                  {row.file_md5 ? (
                                    <div>
                                      <span className="font-medium text-gray-700">File MD5:</span>{' '}
                                      <code className="break-all bg-gray-100 px-2 py-1 rounded" title={row.file_md5}>
                                        {row.file_md5}
                                      </code>
                                    </div>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
                <span>
                  {rows.length.toLocaleString()} upload
                  {rows.length === 1 ? '' : 's'}
                  {` · page ${currentPage} of ${totalPages}`}
                </span>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  disabled={loading}
                  onPageChange={(page) => {
                    goToPage(page);
                    setExpandedId(null);
                  }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FastIdReport;
